import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { setThemed } from './ui/ds/theme';
import { Redirect, useLocation } from './router';
import { useSession } from './session';
import { Landing } from './pages/Landing';
import { LoginPage } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Account } from './pages/Account';
import { AdminPage } from './pages/Admin';
import { CliApprovePage } from './pages/CliApprove';
import { NotFound, PageSplash } from './pages/Shell';
import { UpdateNotice } from './UpdateNotice';
import { ForgotPage, ResetPage } from './pages/Forgot';
import { GuidePage } from './pages/Guide';
import { StartPage } from './pages/Start';
import { SignupPage } from './pages/Signup';
import { PlansPage } from './pages/Plans';
import { LegalPage } from './pages/Legal';
import { mustSetUp, needsPlan } from './api';

const WorkspaceRoute = lazy(() => import('./workspaceEntry'));
const DsLab = import.meta.env.DEV ? lazy(() => import('./pages/DsLab')) : null;

const TITLES: Record<string, string> = {
  '/': 'cmx — your always-on cloud machine for AI agents',
  '/login': 'Sign in · cmx',
  '/signup': 'Create your account · cmx',
  '/plans': 'Pick a plan · cmx',
  '/account': 'Account · cmx',
  '/terms': 'Terms · cmx',
  '/privacy': 'Privacy · cmx',
  '/acceptable-use': 'Acceptable use · cmx',
  '/forgot': 'Forgot password · cmx',
  '/dashboard': 'Dashboard · cmx',
  '/admin': 'Admin · cmx',
  '/cli': 'Approve cmx CLI · cmx',
  '/guide': 'Guide · cmx',
  '/start': 'Getting ready · cmx',
  '/app': 'cmx',
};
const titleFor = (path: string) => (path.startsWith('/reset/') ? 'Choose a new password · cmx' : (TITLES[path] ?? 'cmx'));

/** Pages that need a signed-in user: redirect to /login when signed out. beforeSetup: open while
 *  setup is still to do (Account: the plan, billing and deleting the account). */
function Guarded({ path, beforeSetup, children }: { path: string; beforeSetup?: boolean; children: (isAdmin: boolean) => ReactNode }) {
  const { auth, me } = useSession();
  if (auth.kind === 'out') return <Redirect to={`/login?next=${encodeURIComponent(path)}`} />;
  if (auth.kind === 'error') return <PageSplash title="Can’t reach cmx" detail={`${auth.message} — retrying…`} />;
  if (auth.kind !== 'in' || !me) return <PageSplash />;
  // No plan yet: Plans first. Not set up yet while the gate applies: Getting ready (the hub redirects too).
  if (needsPlan(me)) return <Redirect to="/plans" />;
  if (mustSetUp(me) && !beforeSetup) return <Redirect to="/start" />;
  return <>{children(me.role === 'admin')}</>;
}

/** The workspace waits for setup too (the hub redirects a fresh load; this covers the rest). */
function SetupFirst({ children }: { children: ReactNode }) {
  const { me } = useSession();
  if (mustSetUp(me)) return <Redirect to="/start" />;
  return <>{children}</>;
}

/** Pages on the workspace system: light or dark, following Settings → Appearance. */
const THEMED = new Set(['/app']);

export function App() {
  return (
    <>
      <Routes />
      <UpdateNotice />
    </>
  );
}

function Routes() {
  const { path } = useLocation();
  const view = path === '/app' ? 'app' : 'site';

  useEffect(() => {
    document.documentElement.dataset.view = view;
    document.title = titleFor(path);
  }, [path, view]);

  // The workspace system (ui/ds) themes only the pages built on it; auth and the site keep their own look.
  useEffect(() => {
    if (path === '/ds-lab') return;
    setThemed(THEMED.has(path));
  }, [path]);

  // An internal-tester invite: sign-up with it (spec "Internal testers").
  if (path.startsWith('/invite/')) return <SignupPage invite={path.slice('/invite/'.length)} />;
  if (path.startsWith('/reset/')) return <ResetPage token={path.slice('/reset/'.length)} />;
  switch (path) {
    case '/':
      return <Landing />;
    case '/login':
      return <LoginPage />;
    case '/signup':
      return <SignupPage />;
    case '/plans':
      return <PlansPage />;
    case '/terms':
      return <LegalPage doc="terms" />;
    case '/privacy':
      return <LegalPage doc="privacy" />;
    case '/acceptable-use':
      return <LegalPage doc="acceptable-use" />;
    case '/account':
      return (
        <Guarded path={path} beforeSetup>
          {() => <Account />}
        </Guarded>
      );
    case '/forgot':
      return <ForgotPage />;
    case '/dashboard':
      return <Guarded path={path}>{() => <Dashboard />}</Guarded>;
    case '/admin':
      return <Guarded path={path}>{(isAdmin) => (isAdmin ? <AdminPage /> : <Redirect to="/dashboard" />)}</Guarded>;
    case '/cli':
      return <CliApprovePage />;
    case '/guide':
      return <GuidePage />;
    case '/start':
      return <StartPage />;
    case '/ds-lab':
      if (DsLab)
        return (
          <Suspense fallback={null}>
            <DsLab />
          </Suspense>
        );
      return <NotFound />;
    case '/app':
      return (
        <SetupFirst>
          <Suspense fallback={<div className="splash" aria-busy="true">cmx</div>}>
            <WorkspaceRoute />
          </Suspense>
        </SetupFirst>
      );
    default:
      return <NotFound />;
  }
}
